import React from "react";
import { Link } from "react-router-dom";
import { Download, Clock, ArrowLeft, Mail, CalendarDays } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { arteNaturaFiori, contactInfo } from "../mock/mock";
import { regolamentoDates, regolamentoIntro, regolamentoArticles } from "../data/regolamento";

const L = (lang, it, en) => (lang === "en" ? en : it);

const Body = ({ items }) => (
  <div className="space-y-4 text-[#4a463f] leading-relaxed">
    {items.map((item, i) => {
      if (Array.isArray(item))
        return (
          <ul key={i} className="space-y-1.5 pl-1">
            {item.map((li, j) => (
              <li key={j} className="flex gap-3">
                <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#b5533a]" />
                <span>{li}</span>
              </li>
            ))}
          </ul>
        );
      if (typeof item === "object")
        return <h4 key={i} className="pt-3 font-serif text-xl text-[#1c1b1a]">{item.h}</h4>;
      return <p key={i}>{item}</p>;
    })}
  </div>
);

const DownloadButton = ({ testId, label }) => (
  <a
    href={arteNaturaFiori.contest.rulesUrl}
    target="_blank"
    rel="noopener noreferrer"
    download
    data-testid={testId}
    className="inline-flex items-center gap-2 bg-[#b5533a] hover:bg-[#9a4430] text-white px-6 py-3.5 text-xs uppercase tracking-[0.16em] font-medium transition-colors"
  >
    <Download className="w-4 h-4" /> {label}
  </a>
);

const Regolamento = () => {
  const { lang, pick } = useLang();
  const dlLabel = pick(arteNaturaFiori.contest.rulesLabel);

  return (
    <div data-testid="regolamento-page">
      <PageHero
        image={arteNaturaFiori.heroImage}
        kicker={L(lang, "Arte & Natura · Fiori 2026–2027", "Arte & Natura · Fiori 2026–2027")}
        title={L(lang, "Regolamento «Racconta un fiore»", "«Tell a flower» contest rules")}
        lead={L(lang, "Il concorso fotografico della terza edizione di Arte & Natura.", "The photo contest of the third edition of Arte & Natura.")}
      />

      <section className="bg-[#f7f2ec] py-16 lg:py-20">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <Link to="/arte-e-natura" data-testid="regolamento-back-link" className="inline-flex items-center gap-2 text-sm text-[#b5533a] hover:text-[#1c1b1a] transition-colors">
            <ArrowLeft className="w-4 h-4" /> {L(lang, "Torna ad Arte & Natura", "Back to Arte & Natura")}
          </Link>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="regolamento-dates">
            {regolamentoDates.map((d, i) => (
              <Reveal key={i} delay={i * 80} className="bg-white border border-[#e6ddd2] p-5">
                <CalendarDays className="w-5 h-5 text-[#b5533a]" strokeWidth={1.6} />
                <p className="mt-3 text-xs uppercase tracking-[0.16em] text-[#8a8378]">{pick(d.label)}</p>
                <p className="mt-1 font-serif text-xl text-[#1c1b1a]">{pick(d.date)}</p>
              </Reveal>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-5">
            <DownloadButton testId="regolamento-download-top" label={dlLabel} />
            <span className="inline-flex items-center gap-2 text-sm text-[#4a463f]" data-testid="regolamento-form-soon">
              <Clock className="w-4 h-4 text-[#b5533a]" /> {pick(arteNaturaFiori.contest.soon)}
            </span>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1c1b1a]">{pick(regolamentoIntro.title)}</h2>
            <div className="mt-6"><Body items={pick(regolamentoIntro.body)} /></div>
          </Reveal>

          <div className="mt-14 divide-y divide-[#ece4da]">
            {regolamentoArticles.map((a, i) => (
              <article key={i} className="py-10" data-testid={`regolamento-article-${i + 1}`}>
                <h3 className="flex items-baseline gap-4 font-serif text-2xl md:text-3xl text-[#1c1b1a]">
                  <span className="text-[#b5533a] text-lg tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                  {pick(a.title)}
                </h3>
                <div className="mt-5"><Body items={pick(a.body)} /></div>
              </article>
            ))}
          </div>

          <div className="mt-6 border border-[#e6ddd2] bg-[#f7f2ec] p-7" data-testid="regolamento-contacts">
            <h3 className="font-serif text-2xl text-[#1c1b1a]">{L(lang, "Contatti", "Contacts")}</h3>
            <p className="mt-3 text-[#4a463f]">Arts4All {L(lang, "Associazione Culturale", "Cultural Association")}</p>
            <a href="mailto:info.arts4all@yahoo.com" className="mt-2 inline-flex items-center gap-2 text-[#b5533a] hover:underline" data-testid="regolamento-email-link">
              <Mail className="w-4 h-4" /> info.arts4all@yahoo.com
            </a>
            <p className="mt-2 text-sm text-[#4a463f]">
              <a href={contactInfo.instagram} target="_blank" rel="noopener noreferrer" className="hover:text-[#b5533a]">Instagram @arts4all.italy</a>
              {" · "}
              <a href={contactInfo.facebook} target="_blank" rel="noopener noreferrer" className="hover:text-[#b5533a]">Facebook Arts4All</a>
              {" · "}www.arts-4all.com
            </p>
            <div className="mt-6"><DownloadButton testId="regolamento-download-bottom" label={dlLabel} /></div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Regolamento;
